import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { DropdownModule } from "primeng/dropdown";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { StatsFlightFilter, StatsService } from "@/_services";
import { combineLatest, Observable, of, Subscription } from "rxjs";
import { CommonModule } from "@angular/common";
import { shareReplay, startWith, switchMap, tap, map } from "rxjs/operators";
import { Options } from "chartjs-plugin-datalabels/types/options";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import moment from "moment";
import { StatsSummary } from "@/components/shared/graphics/graphic-interfaces";
import { buildPastDaysDateRange } from "@/_utility/date-range.util";

const lightChartLabelColor = "var(--chart-text)";

const labelOptions: Options = {
    color: lightChartLabelColor,
    font: {
        weight: "bold",
        size: 14,
    },
    display: (context) => {
        const point = context.dataset.data[context.dataIndex] as number;
        return point > 0;
    },
};

@UntilDestroy()
@Component({
    selector: "app-home-asap-performance",
    standalone: true,
    imports: [
        CommonModule,
        SharedComponentsModule,
        DropdownModule,
        FormsModule,
        ReactiveFormsModule,
    ],
    templateUrl: "./home-asap-performance.component.html",
    styleUrl: "./home-asap-performance.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeAsapPerformanceComponent implements OnInit, OnDestroy {
    @Input() asapFormId: string;
    @Input() isAdmin: boolean = false;

    timeFrameList: RadioOption[] = [
        { label: "Past 7 Days", value: 7 },
        { label: "Past 30 Days", value: 30 },
        { label: "Past 60 Days", value: 60 },
        { label: "Past 90 Days", value: 90 },
        { label: "Past Year", value: 365 },
    ];
    timeFrameControl: FormControl<number> = new FormControl<number>(30);
    asapFormControl = new FormControl(null);
    timeFrameSubscription: Subscription;

    stats$: Observable<any>;
    readonly ChartOptions = {
        type: "line",
        plugins: {
            datalabels: labelOptions,
        },
        legend: { display: false },
        aspectRatio: 20,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 5,
                bottom: 5,
                left: 5,
                right: 5,
            },
        },
        scales: {
            yAxes: [
                {
                    ticks: {
                        callback: function (value, index, values) {
                            return index === 0 || index === values.length - 1
                                ? value
                                : "";
                        },
                        beginAtZero: true,
                        stepSize: 5,
                        autoSkip: false,
                        display: false,
                    },
                },
            ],
            xAxes: [
                {
                    ticks: {
                        display: false,
                    },
                },
            ],
        },
    };

    completionAsapDataSet$: Observable<StatsSummary>;
    assignedRiskMitigationAsapDataSet$: Observable<StatsSummary>;
    participationRateDataSet$: Observable<StatsSummary>;

    constructor(private statsService: StatsService) {}

    ngOnInit() {
        this.asapFormControl.setValue(this.asapFormId);

        this.stats$ = combineLatest([
            this.timeFrameControl.valueChanges.pipe(
                startWith(this.timeFrameControl.value),
            ),
            this.asapFormControl.valueChanges.pipe(
                startWith(this.asapFormControl.value),
            ),
        ]).pipe(
            untilDestroyed(this),
            switchMap(([timeFrameValue]) => {
                const filterParams: StatsFlightFilter = {
                    assessment: this.asapFormId,
                    flightDate: [],
                };

                if (timeFrameValue) {
                    const [start, end] = buildPastDaysDateRange(timeFrameValue);
                    filterParams.flightDate = [start, end];
                }

                return this.statsService.getStats(filterParams);
            }),
            shareReplay(1),
            map((stats) => {
                // this is a temporal fix to handle the new API structure for the demo purposes also we need to merge asap performance and crew performance to avoid duplicate code
                if (Array.isArray(stats.data)) {
                    const match = stats.data.find(
                        (item: any) => item.assessmentId === this.asapFormId,
                    );
                    return {
                        ...stats,
                        data: match?.summary || stats.data[0]?.summary || {},
                    };
                }
                return stats;
            }),
            tap((stats) => {
                this.completionAsapDataSet$ = of(
                    stats.data?.CompletionRiskOverTime?.chartData,
                );
                this.assignedRiskMitigationAsapDataSet$ = of(
                    stats.data?.AssignedRiskMitigationOverTime?.chartData,
                );
                this.participationRateDataSet$ = of(
                    stats.data?.SubmissionCountsByHourBucketDynamic?.chartData,
                );
            }),
        );
    }

    ngOnDestroy() {
        this.timeFrameSubscription?.unsubscribe();
    }
}
